import type { CSSProperties, HTMLAttributes } from 'react';

import { SectionLabel } from '../core/SectionLabel';
import { useTranslation } from '../data/translations';

export interface GuidelinePanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style'> {
  readonly label?: string;
  readonly intention?: string;
  readonly routinesLabel?: string;
  readonly routines?: readonly string[];
  readonly style?: CSSProperties;
}

/** « LIGNES DIRECTRICES DU JOUR » : une intention en deux lignes + 3 routines indispensables. */
export function GuidelinePanel({
  label,
  intention,
  routinesLabel,
  routines = [],
  style,
  ...rest
}: GuidelinePanelProps) {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('guidelines.label');
  const resolvedRoutinesLabel = routinesLabel ?? t('guidelines.routines');
  return (
    <div
      {...rest}
      style={{
        background: 'rgba(10,11,12,.86)',
        backdropFilter: 'blur(10px)',
        border: '1px solid var(--accent-line)',
        borderRadius: 'var(--radius-card)',
        padding: '18px 20px 20px',
        boxShadow: 'var(--shadow-card)',
        ...style,
      }}
    >
      <SectionLabel size={10}>{resolvedLabel}</SectionLabel>
      <p
        style={{
          margin: '12px 0 14px',
          fontFamily: 'var(--font-display)',
          fontWeight: 400,
          fontSize: 21,
          lineHeight: 1.24,
          color: 'var(--text-display)',
        }}
      >
        {intention}
      </p>
      <div style={{ borderTop: '1px solid var(--accent-line)', paddingTop: 14 }}>
        <SectionLabel size={9} tone="muted">
          {resolvedRoutinesLabel}
        </SectionLabel>
        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'grid', gap: 8 }}>
          {routines.map((routine) => (
            <li
              key={routine}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--fs-lede)',
                fontWeight: 300,
                color: 'var(--text-body)',
              }}
            >
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: 'var(--accent)',
                  flex: '0 0 auto',
                  transform: 'translateY(-2px)',
                }}
              />
              {routine}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
